/**
 * BYOK Cost Alerts
 *
 * Configurable budget thresholds for BYOK (Bring Your Own Key) spending.
 * Shows spending progress and allows per-profile alert configuration.
 *
 * @module @humanizer/studio/components/settings/ByokCostAlerts
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocalAdmin } from '../../hooks/useLocalAdmin';

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export function ByokCostAlerts() {
  const { getByokAlerts, checkByokSpending, setByokAlerts } = useLocalAdmin();

  const [alerts, setAlerts] = useState<any>(null);
  const [spending, setSpending] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    const [a, s] = await Promise.all([
      getByokAlerts(),
      checkByokSpending(),
    ]);
    setAlerts(a);
    setSpending(s);
  }, [getByokAlerts, checkByokSpending]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = useCallback(async () => {
    if (!alerts) return;
    setSaving(true);
    await setByokAlerts(alerts);
    setSaving(false);
  }, [setByokAlerts, alerts]);

  if (!alerts) return null;

  const percentUsed = spending?.percentUsed ?? 0;
  const progressModifier = percentUsed >= 90 ? '--danger'
    : percentUsed >= alerts.warningThreshold ? '--warning' : '';

  return (
    <div className="settings-card">
      <h3 className="settings-card__title">BYOK Cost Alerts</h3>
      <div className="settings-card__content">
        <div className="settings-form-group">
          <label className="settings-form-group__label">
            <input
              type="checkbox"
              checked={alerts.enabled}
              onChange={(e) => setAlerts({ ...alerts, enabled: e.target.checked })}
            />
            {' '}Enable monthly budget alerts
          </label>
        </div>

        {alerts.enabled && (
          <>
            <div className="settings-form-group">
              <label className="settings-form-group__label">Monthly Budget (millicents)</label>
              <input
                type="number"
                className="settings-form-group__input"
                value={alerts.monthlyBudget}
                min={0}
                step={1000}
                onChange={(e) => setAlerts({
                  ...alerts,
                  monthlyBudget: parseInt(e.target.value, 10) || 0,
                })}
              />
              <span className="settings-form-group__hint">
                ${(alerts.monthlyBudget / 100000).toFixed(2)} / month
              </span>
            </div>

            <div className="settings-form-group">
              <label className="settings-form-group__label">
                Warning at {alerts.warningThreshold}%
              </label>
              <input
                type="range"
                className="settings-form-group__range"
                min={50}
                max={100}
                value={alerts.warningThreshold}
                onChange={(e) => setAlerts({
                  ...alerts,
                  warningThreshold: parseInt(e.target.value, 10),
                })}
              />
            </div>

            {spending && !spending.error && (
              <div className="usage-progress">
                <div className="usage-progress__header">
                  <span>Spent: ${((spending.spent || 0) / 100000).toFixed(2)}</span>
                  <span>Budget: ${(alerts.monthlyBudget / 100000).toFixed(2)}</span>
                </div>
                <div className="usage-progress__bar">
                  <div
                    className={`usage-progress__fill${progressModifier}`}
                    style={{ width: `${Math.min(percentUsed, 100)}%` }}
                  />
                </div>
              </div>
            )}
          </>
        )}

        <button
          className="settings-button settings-button--primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Alert Settings'}
        </button>
      </div>
    </div>
  );
}
